import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Cpu, FileCheck2, Download, Sparkles, Loader2, Lock, Zap, Check } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

export default function DeviceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isPro = user?.plan && user.plan !== "free";
  const [project, setProject] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/devices/${id}`);
    setProject(data);
  };

  useEffect(() => { load(); }, [id]);

  const classify = async () => {
    setClassifying(true);
    try {
      await api.post(`/devices/${id}/classify`);
      toast.success("Classification complete");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Classification failed");
    } finally {
      setClassifying(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/devices/${id}/generate-qms`);
      toast.success("QMS blueprint generated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "QMS generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (!project) {
    return (
      <DashboardLayout>
        <div className="p-10 text-slate-500">Loading…</div>
      </DashboardLayout>
    );
  }

  const c = project.classification;
  const q = project.qms;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="vq-overline mb-2 font-mono">{project.project_id}</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
          {project.device_name}
        </h1>
        <p className="text-slate-600 max-w-3xl mb-6">{project.intended_use}</p>
        <div className="flex flex-wrap gap-2 mb-8">
          {project.markets?.map((m) => (
            <span key={m} className="px-2 py-1 text-xs font-mono border border-slate-300 rounded text-slate-700 bg-white">{m}</span>
          ))}
          <span className="px-2 py-1 text-xs font-mono border border-slate-300 rounded text-slate-700 bg-white">
            {project.device_type}
          </span>
          {project.software_involved && (
            <span className="px-2 py-1 text-xs font-mono border border-slate-300 rounded text-slate-700 bg-white">IEC 62304</span>
          )}
          {project.ai_ml_involved && (
            <span className="px-2 py-1 text-xs font-mono border border-slate-300 rounded text-slate-700 bg-white">AI/ML</span>
          )}
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-3 mb-10">
          <Button
            onClick={classify}
            disabled={classifying}
            data-testid="classify-btn"
            className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold"
          >
            {classifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Cpu className="w-4 h-4 mr-2" />}
            {c ? "Re-classify" : "Run classification"}
          </Button>
          {isPro ? (
            <Button
              onClick={generate}
              disabled={generating || !c}
              data-testid="generate-qms-btn"
              variant="outline"
              className="border-slate-300 font-semibold"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}
              {q ? "Regenerate QMS" : "Generate QMS blueprint"}
            </Button>
          ) : c && (
            <Link to="/pricing" data-testid="upgrade-for-qms-btn">
              <Button
                variant="outline"
                className="border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white font-semibold"
              >
                <Lock className="w-4 h-4 mr-2" /> Unlock QMS blueprint — Upgrade to Pro
              </Button>
            </Link>
          )}
        </div>

        {/* Classification */}
        {c && (
          <section className="mb-12">
            <div className="vq-overline mb-3">Classification Engine</div>
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-6">Global Classification</h2>
            <p className="text-slate-600 mb-6 max-w-3xl">{c.summary}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <ClassCard label="FDA (US)" cls={c.fda?.class} pathway={c.fda?.pathway} rationale={c.fda?.rationale} />
              <ClassCard label="EU MDR" cls={c.eu_mdr?.class} pathway={c.eu_mdr?.rule} rationale={c.eu_mdr?.rationale} />
              <ClassCard label="India CDSCO" cls={c.india_cdsco?.class} rationale={c.india_cdsco?.rationale} />
              <ClassCard label="MDSAP" cls={c.mdsap?.applicable ? "Applicable" : "N/A"} rationale={c.mdsap?.notes} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="vq-card p-6">
                <div className="vq-overline mb-2">Risk Level</div>
                <div className="font-display font-extrabold text-2xl text-slate-900">{c.risk_level}</div>
              </div>
              <div className="vq-card p-6">
                <div className="vq-overline mb-2">IEC 62304 Software Class</div>
                <div className="font-display font-extrabold text-2xl text-slate-900">{c.samd_iec_62304_class || "N/A"}</div>
              </div>
            </div>
          </section>
        )}

        {/* QMS */}
        {q && (
          <section className="mb-12">
            <div className="flex items-end justify-between mb-6 gap-4">
              <div>
                <div className="vq-overline mb-3">QMS Blueprint</div>
                <h2 className="font-display font-bold text-2xl text-slate-900">Generated QMS</h2>
              </div>
              <Button variant="outline" data-testid="export-qms-btn" onClick={() => toast.info("510(k) Package export queued")} className="border-slate-300 font-semibold">
                <Download className="w-4 h-4 mr-2" /> Export 510(k) package
              </Button>
            </div>
            <p className="text-slate-600 mb-6 max-w-3xl">{q.summary}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard label="Compliance Score" value={`${q.compliance_score}%`} />
              <StatCard label="SOPs Ready" value={q.sops?.length || 0} />
              <StatCard label="Hazards ID'd" value={q.risk_matrix?.hazards_identified || 0} />
              <StatCard label="High Risk Items" value={q.risk_matrix?.high_risk_items || 0} tone="risk" />
            </div>

            {/* SOPs */}
            <div className="vq-card p-6 mb-6">
              <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Standard Operating Procedures</h3>
              <Accordion type="single" collapsible className="w-full">
                {(q.sops || []).slice(0, 15).map((sop, i) => (
                  <AccordionItem key={sop.id || i} value={sop.id || `sop-${i}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-4 text-left">
                        <span className="font-mono text-xs text-slate-500">{sop.id}</span>
                        <span className="font-semibold text-slate-900">{sop.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-sm text-slate-600 space-y-2">
                        <div><span className="font-mono text-xs uppercase tracking-wider text-slate-500">Category:</span> {sop.category}</div>
                        <div><span className="font-mono text-xs uppercase tracking-wider text-slate-500">Reference:</span> {sop.iso_ref}</div>
                        <div><span className="font-mono text-xs uppercase tracking-wider text-slate-500">Status:</span> {sop.status}</div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* DHF */}
            <div className="vq-card p-6 mb-6">
              <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Design History File (DHF)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(q.dhf?.sections || []).map((s) => (
                  <div key={s} className="border border-slate-200 rounded-md p-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{s}</span>
                    <span className="text-xs font-mono text-emerald-700">✓ template</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Submissions */}
            {q.regulatory_submissions?.length > 0 && (
              <div className="vq-card p-6">
                <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Regulatory Submissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {q.regulatory_submissions.map((s, i) => (
                    <div key={`${s.market}-${s.type}-${i}`} className="border border-slate-200 rounded-md p-4">
                      <div className="vq-overline mb-2">{s.market}</div>
                      <div className="font-display font-bold text-slate-900">{s.type}</div>
                      <div className="text-xs text-slate-500 font-mono mt-1">~{s.estimated_timeline_days} days</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {!c && !classifying && (
          <div className="vq-card p-10 text-center">
            <Sparkles className="w-8 h-8 text-[#0055FF] mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-slate-900 mb-2">Ready to classify</h3>
            <p className="text-slate-600 max-w-md mx-auto">
              Run the AI classification engine to get FDA / EU / India / MDSAP class assignments.
            </p>
          </div>
        )}

        {/* Paywall preview — shown when classified but on free plan & no QMS yet */}
        {c && !q && !isPro && <PaywallPreview />}

        {/* Multi-market Submission Matrix */}
        <SubmissionMatrix pid={project.project_id} />

        {/* Risk Register — trend sparkline + Sev/Prob heatmap. */}
        <RiskTrend deviceId={project.project_id} />
        <RiskHeatmap deviceId={project.project_id} />
      </div>
    </DashboardLayout>
  );
}

function SubmissionMatrix({ pid }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!pid) return;
    api.get(`/devices/${pid}/submission-matrix`).then(({ data }) => setData(data))
      .catch((e) => logger.warn("Matrix:", e?.response?.status));
  }, [pid]);
  if (!data) return null;
  return (
    <div className="vq-card p-5 mt-6" data-testid="submission-matrix">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="vq-overline">Multi-market submission matrix</div>
          <div className="font-display font-bold text-slate-900 mt-1">Which markets can we file in this quarter?</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Overall readiness</div>
          <div className="font-display font-extrabold text-3xl text-slate-900">{data.overall_readiness.toFixed(0)}</div>
        </div>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-6">
          No target markets selected on this device. Edit the device to add markets and see per-market readiness.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Market</th>
                <th className="text-left px-3 py-2">Authority</th>
                <th className="text-left px-3 py-2">Primary pathway</th>
                <th className="text-left px-3 py-2">QMS reference</th>
                <th className="text-left px-3 py-2">Docs approved</th>
                <th className="text-left px-3 py-2">Readiness</th>
                <th className="text-left px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody data-testid="matrix-tbody">
              {data.rows.map((r) => (
                <tr key={r.market} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{r.market}<span className="text-slate-400 ml-1">({r.region})</span></td>
                  <td className="px-3 py-2 text-xs">{r.authority}</td>
                  <td className="px-3 py-2 text-xs">{r.primary_pathway}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.quality_system}</td>
                  <td className="px-3 py-2 text-xs">{r.docs_approved}/{r.docs_total}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="w-24 h-1.5 bg-slate-100 rounded">
                      <div className="h-full rounded"
                           style={{ width: `${r.readiness_score}%`,
                                    backgroundColor: r.readiness_score >= 85 ? "#059669" :
                                                     r.readiness_score >= 60 ? "#0055FF" :
                                                     r.readiness_score >= 30 ? "#D97706" : "#DC2626" }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{r.readiness_score.toFixed(0)}%</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-amber-700">{r.highlight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-2">{data.disclaimer}</div>
    </div>
  );
}

function PaywallPreview() {
  const previewSOPs = [
    { id: "SOP-001", title: "Document Control Procedure", ref: "ISO 13485 §4.2.4" },
    { id: "SOP-002", title: "Design & Development Planning", ref: "ISO 13485 §7.3.2" },
    { id: "SOP-003", title: "Risk Management Process", ref: "ISO 14971:2019" },
  ];
  const blurredSOPs = [
    { id: "SOP-004", title: "Design Inputs & Requirements", ref: "ISO 13485 §7.3.3" },
    { id: "SOP-005", title: "Design Verification & Validation", ref: "ISO 13485 §7.3.6" },
    { id: "SOP-006", title: "Software Life Cycle (IEC 62304)", ref: "IEC 62304:2006/A1" },
    { id: "SOP-007", title: "Usability Engineering (IEC 62366)", ref: "IEC 62366-1:2015" },
    { id: "SOP-008", title: "Post-Market Surveillance", ref: "ISO 13485 §8.2.1" },
    { id: "SOP-009", title: "CAPA — Corrective & Preventive Action", ref: "ISO 13485 §8.5.2" },
    { id: "SOP-010", title: "Supplier Qualification & Purchasing", ref: "ISO 13485 §7.4" },
    { id: "SOP-011", title: "Complaint Handling", ref: "ISO 13485 §8.2.2" },
    { id: "SOP-012", title: "Internal Audit", ref: "ISO 13485 §8.2.4" },
    { id: "SOP-013", title: "Cybersecurity — FDA Premarket Guidance", ref: "FDA 2023 Guidance" },
  ];

  return (
    <section className="mb-12" data-testid="paywall-preview">
      <div className="vq-overline mb-3">QMS Blueprint • Preview</div>
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <h2 className="font-display font-bold text-2xl text-slate-900">
          You&apos;re 3 clicks from a submission-ready QMS.
        </h2>
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-xs font-mono uppercase tracking-wider border border-[#0055FF]/30">
          <Lock className="w-3 h-3" /> Pro feature
        </span>
      </div>
      <p className="text-slate-600 mb-8 max-w-3xl">
        Based on your classification, VitaQMS will auto-generate <strong>~25 SOPs</strong>, a complete
        <strong> ISO 14971 risk file</strong>, and a <strong>DHF</strong> ready for FDA 510(k) submission — in about 45 seconds.
      </p>

      {/* Preview SOPs (unblurred) */}
      <div className="vq-card p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900">Preview: first 3 SOPs</h3>
          <span className="text-xs font-mono text-emerald-700">✓ Unlocked</span>
        </div>
        <div className="space-y-2">
          {previewSOPs.map((s) => (
            <div key={s.id} className="flex items-center gap-4 border border-slate-200 rounded-md p-3">
              <Check className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-mono text-xs text-slate-500 w-16 shrink-0">{s.id}</span>
              <span className="font-semibold text-slate-900 flex-1">{s.title}</span>
              <span className="text-xs font-mono text-slate-500 hidden md:block">{s.ref}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Blurred remainder + overlay CTA */}
      <div className="relative">
        <div className="vq-card p-6 select-none pointer-events-none" style={{ filter: "blur(4px)", opacity: 0.6 }}>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">+ 22 more SOPs, DHF & Export</h3>
          <div className="space-y-2">
            {blurredSOPs.map((s) => (
              <div key={s.id} className="flex items-center gap-4 border border-slate-200 rounded-md p-3">
                <Lock className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="font-mono text-xs text-slate-500 w-16 shrink-0">{s.id}</span>
                <span className="font-semibold text-slate-900 flex-1">{s.title}</span>
                <span className="text-xs font-mono text-slate-500 hidden md:block">{s.ref}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
            {["FDA 510(k)", "EU MDR Technical File", "CDSCO Registration"].map((x) => (
              <div key={x} className="border border-slate-200 rounded-md p-4">
                <div className="vq-overline mb-2">{x}</div>
                <div className="font-display font-bold text-slate-900">Submission-ready</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="vq-card p-8 max-w-lg text-center shadow-2xl shadow-slate-900/20 border-2 border-slate-900">
            <div className="w-12 h-12 bg-[#0055FF] rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="vq-overline mb-2">Unlock full QMS</div>
            <h3 className="font-display font-extrabold text-2xl text-slate-900 mb-3">
              Upgrade to Pro
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              22 more SOPs • Complete DHF • ISO 14971 risk file • 510(k) package export • Regulatory alerts • $99/month.
            </p>
            <div className="space-y-2 text-left mb-6 text-sm">
              {[
                "Full auto-generated QMS blueprint (~25 SOPs)",
                "DHF sections mapped to Design Controls",
                "One-click 510(k) / MDR Technical File export",
                "Priority AI compute (45 sec generation)",
              ].map((f) => (
                <div key={f} className="flex items-start gap-2 text-slate-700">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                </div>
              ))}
            </div>
            <Link to="/pricing" data-testid="paywall-upgrade-btn">
              <Button className="w-full h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold rounded-md">
                Upgrade to Pro — $99/mo
              </Button>
            </Link>
            <p className="text-[10px] font-mono text-slate-400 mt-3 uppercase tracking-wider">
              India + US billing • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClassCard({ label, cls, pathway, rationale }) {
  return (
    <div className="vq-card p-5">
      <div className="vq-overline mb-2">{label}</div>
      <div className="font-display font-extrabold text-2xl text-slate-900">{cls || "—"}</div>
      {pathway && <div className="text-xs font-mono text-[#0055FF] mt-1">{pathway}</div>}
      {rationale && <div className="text-xs text-slate-500 mt-3 line-clamp-3">{rationale}</div>}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`vq-card p-5 ${tone === "risk" ? "bg-red-50 border-red-200" : ""}`}>
      <div className="vq-overline mb-1">{label}</div>
      <div className="font-display font-extrabold text-2xl text-slate-900">{value}</div>
    </div>
  );
}

/**
 * RiskTrend — small sparkline showing residual-risk count per ISO week
 * for the last 90 days on this device. Pure SVG so we don't pull in a
 * chart lib just for one 200×48 sparkline. Colour tint tells the story:
 * emerald when the register shrinks week-over-week, red when it grows,
 * slate when flat.
 */
function RiskTrend({ deviceId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!deviceId) return;
    api.get(`/risks/summary/trend?device_id=${encodeURIComponent(deviceId)}&days=90`)
      .then(({ data }) => setData(data))
      .catch((e) => logger.warn("risk trend:", e?.response?.status));
  }, [deviceId]);

  if (!data) return null;
  const buckets = data.buckets || [];

  // Trend direction — compare last bucket to the median of prior buckets.
  let trendCls = "text-slate-500", trendIcon = "→", trendLabel = "flat";
  if (buckets.length >= 2) {
    const last = buckets[buckets.length - 1].total;
    const prior = buckets.slice(0, -1).map((b) => b.total).sort((a, b) => a - b);
    const median = prior.length ? prior[Math.floor(prior.length / 2)] : 0;
    if (last > median) { trendCls = "text-red-700"; trendIcon = "↑"; trendLabel = `up ${last - median}`; }
    else if (last < median) { trendCls = "text-emerald-700"; trendIcon = "↓"; trendLabel = `down ${median - last}`; }
  }

  // Render sparkline — 200×48 viewport, 4px inset so top/bottom points
  // aren't clipped. Empty window → friendly nudge.
  const W = 220, H = 48, PAD = 4;
  const totals = buckets.map((b) => b.total);
  const max = Math.max(1, ...totals);
  const pts = buckets.map((b, i) => {
    const x = PAD + (i * (W - 2 * PAD)) / Math.max(1, buckets.length - 1);
    const y = H - PAD - ((b.total / max) * (H - 2 * PAD));
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div className="vq-card p-5 mt-6" data-testid="risk-trend">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="vq-overline">Risk register</div>
          <div className="font-display font-bold text-slate-900 mt-1">Residual risk trend · last 90d</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {data.total_in_window} risk{data.total_in_window === 1 ? "" : "s"} added in window
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`text-sm font-mono ${trendCls}`} data-testid="risk-trend-direction">
            {trendIcon} {trendLabel}
          </div>
          <Link
            to={`/devices/${deviceId}/risk-trend`}
            className="text-xs font-semibold text-vq-blue hover:underline"
            data-testid="risk-trend-zoom"
          >
            Zoom →
          </Link>
        </div>
      </div>
      {buckets.length === 0 ? (
        <div className="text-center py-4 text-sm text-slate-500" data-testid="risk-trend-empty">
          No risks created in the last 90 days on this device.
        </div>
      ) : (
        <Link
          to={`/devices/${deviceId}/risk-trend`}
          className="block cursor-pointer hover:opacity-80 transition"
          aria-label="Open full risk trend view"
          data-testid="risk-trend-spark-link"
        >
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="60" data-testid="risk-trend-spark" aria-hidden="true">
          <polyline
            fill="none"
            stroke={trendCls.includes("red") ? "#dc2626" : trendCls.includes("emerald") ? "#059669" : "#0055FF"}
            strokeWidth="1.8"
            points={pts}
          />
          {buckets.map((b, i) => {
            const x = PAD + (i * (W - 2 * PAD)) / Math.max(1, buckets.length - 1);
            const y = H - PAD - ((b.total / max) * (H - 2 * PAD));
            return (
              <circle
                key={`spark-dot-${b.week}`}
                cx={x}
                cy={y}
                r={2.5}
                fill={trendCls.includes("red") ? "#dc2626" : trendCls.includes("emerald") ? "#059669" : "#0055FF"}
              >
                <title>{`${b.week} · ${b.total} risk${b.total === 1 ? "" : "s"} added (H:${b.High} M:${b.Medium} L:${b.Low})`}</title>
              </circle>
            );
          })}
        </svg>
        </Link>
      )}
    </div>
  );
}

/**
 * RiskHeatmap — Severity × Probability grid for one device.
 *
 * Fills a 3×3 matrix (Low/Med/High along each axis) with the count of risks
 * in each bucket. Cell colour scales with the combined ISO 14971 risk
 * category so QA can spot clusters at a glance: Sev×Prob = High/High glows
 * red, Low/Low stays green. Clicking a cell deep-links to /risk-register
 * pre-filtered to that device. Empty state renders a friendly nudge with a
 * one-tap route to the register.
 */
function RiskHeatmap({ deviceId }) {
  const [risks, setRisks] = useState(null);
  useEffect(() => {
    if (!deviceId) return;
    api.get(`/risks?device_id=${encodeURIComponent(deviceId)}`)
      .then(({ data }) => setRisks(data))
      .catch((e) => logger.warn("Risks:", e?.response?.status));
  }, [deviceId]);

  if (!risks) return null;

  // Bucket risks into a 3×3 matrix keyed by (severity, probability).
  const LEVELS = ["Low", "Medium", "High"];
  const grid = {};
  LEVELS.forEach((s) => { grid[s] = {}; LEVELS.forEach((p) => { grid[s][p] = []; }); });
  risks.forEach((r) => {
    const s = LEVELS.includes(r.severity) ? r.severity : "Medium";
    const p = LEVELS.includes(r.probability) ? r.probability : "Medium";
    grid[s][p].push(r);
  });

  // ISO 14971 combined risk category — colour cells by aggregate.
  const cellCategory = (s, p) => {
    if (s === "High" && p !== "Low") return "high";
    if (s === "High" || (s === "Medium" && p === "High")) return "high";
    if (s === "Medium" && p === "Medium") return "medium";
    if (s === "Low" && p === "Low") return "low";
    return "medium";
  };
  const cellCls = (cat, count) => {
    const strong = count > 0;
    if (cat === "high") return strong ? "bg-red-100 border-red-300 text-red-900" : "bg-red-50/40 border-red-100 text-red-300";
    if (cat === "medium") return strong ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-amber-50/40 border-amber-100 text-amber-300";
    return strong ? "bg-emerald-100 border-emerald-300 text-emerald-900" : "bg-emerald-50/40 border-emerald-100 text-emerald-300";
  };

  return (
    <div className="vq-card p-5 mt-6" data-testid="risk-heatmap">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="vq-overline">ISO 14971</div>
          <div className="font-display font-bold text-slate-900 mt-1">Risk heatmap · Severity × Probability</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {risks.length} risk{risks.length === 1 ? "" : "s"} on this device
          </div>
        </div>
        <Link
          to={`/risk-register?device=${encodeURIComponent(deviceId)}`}
          className="text-xs font-semibold text-[#0055FF] hover:underline"
          data-testid="risk-heatmap-open-register"
        >
          Open register →
        </Link>
      </div>

      {risks.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-500" data-testid="risk-heatmap-empty">
          No risks logged for this device yet.
          <Link to={`/risk-register?device=${encodeURIComponent(deviceId)}`} className="text-[#0055FF] hover:underline ml-1">
            Start the register →
          </Link>
        </div>
      ) : (
        <div className="inline-block">
          <div className="grid grid-cols-[auto_repeat(3,minmax(90px,1fr))] gap-1.5 text-center text-xs">
            <div key="heatmap-corner" />
            {LEVELS.map((p) => (
              <div key={`col-${p}`} className="font-mono uppercase text-[10px] text-slate-500 pb-1 tracking-wider">Prob · {p}</div>
            ))}
            {LEVELS.slice().reverse().map((s) => (
              <div key={`heatmap-row-${s}`} className="contents">
                <div className="font-mono uppercase text-[10px] text-slate-500 self-center text-right pr-1 tracking-wider">Sev · {s}</div>
                {LEVELS.map((p) => {
                  const items = grid[s][p];
                  const cat = cellCategory(s, p);
                  return (
                    <Link
                      key={`cell-${s}-${p}`}
                      to={`/risk-register?device=${encodeURIComponent(deviceId)}&severity=${s}&probability=${p}`}
                      className={`border rounded p-3 font-mono transition-colors hover:brightness-95 ${cellCls(cat, items.length)}`}
                      data-testid={`risk-heatmap-cell-${s}-${p}`}
                      title={items.length ? items.map((r) => r.title).join("\n") : `No risks at Sev=${s} · Prob=${p}`}
                    >
                      <div className="font-display font-bold text-2xl leading-none">{items.length}</div>
                      <div className="text-[9px] uppercase tracking-wider opacity-70 mt-1">{cat === "high" ? "High" : cat === "medium" ? "Medium" : "Low"}</div>
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

